import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useFormValidation from '@/hooks/useFormValidation';
import { useGetSupplierSettings } from '@/api/hooks/supplier-settings/useGetSupplierSettings';
import { useUpdateSupplierSettings } from '@/api/hooks/supplier-settings/useUpdateSupplierSettings';
import {
  normalizeClientWebsiteLabel,
  normalizeClientWebsiteUrl,
} from '@/utils/url.util';

const CLIENT_SECRET_REQUIRED_MESSAGE =
  'client_secret is required when enabling a supplier lookup connection for the first time';

const DEFAULT_FORM = {
  enabled: false,
  token_url: '',
  client_id: '',
  client_secret: '',
  base_rest_url: '',
  oauth_scope: '',
  client_website_url: '',
  client_website_label: '',
};

export const useSupplierPortalSettingsForm = (organizationId) => {
  const { data: settings, loading } = useGetSupplierSettings(organizationId);
  const { updateSettings, loading: saving } = useUpdateSupplierSettings();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [hasClientSecret, setHasClientSecret] = useState(false);

  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    setClientFieldErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();

  const applySettings = (data) => {
    setForm({
      enabled: data?.enabled ?? false,
      token_url: data?.token_url ?? '',
      client_id: data?.client_id ?? '',
      client_secret: '',
      base_rest_url: data?.base_rest_url ?? '',
      oauth_scope: data?.oauth_scope ?? '',
      client_website_url: normalizeClientWebsiteUrl(data?.client_website_url),
      client_website_label: normalizeClientWebsiteLabel(
        data?.client_website_label,
      ),
    });
    setHasClientSecret(data?.has_client_secret ?? false);
  };

  useEffect(() => {
    if (!loading) {
      applySettings(settings);
    }
  }, [loading, settings]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    clearFieldError(name);
  };

  const setEnabled = (value) => {
    setForm((current) => ({ ...current, enabled: value }));
    clearFieldError('enabled');
  };

  const clientSecretMissing =
    form.enabled && !hasClientSecret && !form.client_secret.trim();

  const handleUpdate = async () => {
    clearAllErrors();

    const payload = {
      provider: 'oracle',
      enabled: form.enabled,
      token_url: form.token_url.trim() || null,
      client_id: form.client_id.trim() || null,
      ...(form.client_secret.trim()
        ? { client_secret: form.client_secret }
        : {}),
      base_rest_url: form.base_rest_url.trim() || null,
      oauth_scope: form.oauth_scope.trim() || null,
      client_website_url:
        normalizeClientWebsiteUrl(form.client_website_url) || null,
      client_website_label:
        normalizeClientWebsiteLabel(form.client_website_label) || null,
    };

    try {
      const res = await updateSettings(organizationId, payload);
      applySettings(res.data);
      toast.success('Supplier Portal settings updated successfully');
    } catch (error) {
      const data = error.response?.data;
      if (data?.error_code === 'SUPPLIER_LOOKUP_SECRET_REQUIRED') {
        setClientFieldErrors({ client_secret: data.message });
      } else if (
        error.response?.status === 400 ||
        error.response?.status === 404
      ) {
        setBackendErrors(data);
        if (clientSecretMissing) {
          setClientFieldErrors({
            client_secret: CLIENT_SECRET_REQUIRED_MESSAGE,
          });
        }
      } else {
        toast.error('Failed to update Supplier Portal settings');
      }
    }
  };

  return {
    loading,
    saving,
    form,
    hasClientSecret,
    fieldErrors,
    generalErrors,
    updateField,
    setEnabled,
    handleUpdate,
  };
};
